'use strict';

import { updateUIFromShadowRoot, translate } from '@core/i18n/window-language';
import {
    UPDATE_LANGUAGE_EVENT,
    UPDATE_ORDER_BY_EVENT,
    SYNCH_TYPE_FILTER_SEARCH_OPTIONS_EVENT,
    TRIGGER_RESET_FILTERS_EVENT,
    SEARCH_REPORT_DETAILS_RESULT_EVENT,
    SEARCH_IMG_RESULT_EVENT,
} from '@core/constants/EVENTS';
import {
    globalSharedStyles,
    customCommonStyles,
    typographySharedStyles,
    customMenuDownStyleSheet,
    commonTabsLayoutStyles,
} from '@core/styles';
import { isReportDetailsView } from '@core/utils/report';
import {
    SEARCH_IMGS_ORDER_BY_OPTIONS,
    DEFAULT_ORDER_BY_OPTION,
    SEARCHER_TYPES,
} from '@core/constants/SEARCHER';
import { SEARCH_IMG_QUERY_PARAMS } from '@core/constants/API';
import { dispatchDetailIbj } from '@core/utils/searcher';
import { ripple } from '@core/utils/ripple';
import {
    ORDER_BY_OPTIONS_ATTRIBUTES,
    DEFAULT_ORDER_BY_OPTIONS,
    UPDATE_VALUE_ORDER_BY_EVENT,
} from './CONSTANTS';
import {
    updateOrderButtonLabel,
    initOrderByOptions,
    showOrderByOptions,
    updateCurrentSearcherObj,
    checkCurrentSearcherObj,
    handleOrderByOptionSelection,
    handleFilterSync,
    handleResetFilters,
    handleSearchImageResult,
} from './utils';

import htmlContent from './OrderBySelectComponent.html';
import cssContent from './OrderBySelectComponent.css';

/**
 * OrderBySelectComponent
 *
 * A web component that provides order by functionality for search results.
 * Allows users to sort results by different criteria like newest, oldest, most similar, etc.
 * Supports dynamic option visibility based on search type and integrates with the search system.
 *
 * @class OrderBySelectComponent
 * @extends {HTMLElement}
 */
class OrderBySelectComponent extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });

        /** @type {HTMLTemplateElement} */
        const template = document.createElement('template');
        template.innerHTML = htmlContent;

        const styleSheet = new CSSStyleSheet();
        styleSheet.replaceSync(cssContent);

        this.shadowRoot.adoptedStyleSheets = [
            globalSharedStyles,
            customCommonStyles,
            typographySharedStyles,
            customMenuDownStyleSheet,
            commonTabsLayoutStyles,
            styleSheet,
        ];

        this.shadowRoot.appendChild(template.content.cloneNode(true));

        // Initialize properties
        this._orderByOptions = null;
        this._currentOrderByOption = null;
        this._orderByButton = null;
        this._orderByOptionsContainer = null;
        this._orderByOptionsElements = null;
        this._overlayContainer = null;
        this._isReportDetailsView = null;
        this._isReportsView = null;

        // Bind event handlers
        this._handleTranslationHelperBound = this._handleTranslationHelper.bind(this);
        this._handleOrderByButtonClickBound = this._handleOrderByButtonClick.bind(this);
        this._handleOverlayClickBound = this._handleOverlayClick.bind(this);
        this._handleOrderByOptionClickBound = this._handleOrderByOptionClick.bind(this);
        this._handleUpdateOrderByEventBound = this._handleUpdateOrderByEvent.bind(this);
        this._handleFilterSyncEventBound = this._handleFilterSyncEvent.bind(this);
        this._handleResetFiltersEventBound = this._handleResetFiltersEvent.bind(this);
        this._handleSearchReportDetailsResultEventBound =
            this._handleSearchReportDetailsResultEvent.bind(this);
        this._handleSearchImgResultEventBound =
            this._handleSearchImgResultEvent.bind(this);
        this._handleUpdateValueOrderByEventBound =
            this._handleUpdateValueOrderByEvent.bind(this);
    }

    /**
     * Called when the element is added to the DOM.
     * Initializes the component and sets up event listeners.
     * @returns {void}
     */
    connectedCallback() {
        this._initializeComponent();
        this._cacheDOMElements();
        this._initializeOrderByOptions();
        this._checkCurrentSearcherObj();
        this._setupReportDetailsView();
        this._showOrderByOptions();
        this._addEventListeners();
    }

    /**
     * Initialize component properties and state.
     * @returns {void}
     * @private
     */
    _initializeComponent() {
        this._orderByOptions =
            this.getAttribute(ORDER_BY_OPTIONS_ATTRIBUTES.ORDER_BY_OPTIONS)?.split(',') ||
            DEFAULT_ORDER_BY_OPTIONS;
        this._currentOrderByOption = DEFAULT_ORDER_BY_OPTION;
        this._isReportDetailsView = isReportDetailsView();
        this._isReportsView = false; // This could be determined from URL or other logic
    }

    /**
     * Cache DOM references for better performance.
     * @returns {void}
     * @private
     */
    _cacheDOMElements() {
        this._orderByButton = this.shadowRoot.querySelector('[data-order-by-button]');
        this._orderByOptionsContainer = this.shadowRoot.querySelector(
            '[data-order-by-options]',
        );
        this._orderByOptionsElements = this.shadowRoot.querySelectorAll(
            '[data-order-by-options] li',
        );
        this._overlayContainer = this.shadowRoot.querySelector('.overlay');
    }

    /**
     * Initialize order by options in the DOM.
     * @param {Object} options - Configuration options.
     * @param {Array<string>} options.orderOptions - The order options to display.
     * @returns {void}
     * @private
     */
    _initializeOrderByOptions({ orderOptions = this._orderByOptions } = {}) {
        const orderByOptionsUlContainer = this.shadowRoot.querySelector(
            '[data-order-by-options] > ul',
        );
        if (!orderByOptionsUlContainer) return;

        initOrderByOptions(orderByOptionsUlContainer, orderOptions);
        this._orderByOptionsElements = this.shadowRoot.querySelectorAll(
            '[data-order-by-options] li',
        );
    }

    /**
     * Setup report details view specific configuration.
     * @returns {void}
     * @private
     */
    _setupReportDetailsView() {
        if (!this._isReportDetailsView) return;

        this._initializeOrderByOptions({
            orderOptions: [
                SEARCH_IMGS_ORDER_BY_OPTIONS.NEW,
                SEARCH_IMGS_ORDER_BY_OPTIONS.OLD,
                SEARCH_IMGS_ORDER_BY_OPTIONS.CAP,
            ],
        });

        this._orderByOptionsElements.forEach((option) => {
            option.classList.remove('selected');
            if (option.getAttribute('value') === this._currentOrderByOption) {
                option.classList.add('selected');
            }
        });
    }

    /**
     * Show order by options with proper visibility and selection.
     * @returns {void}
     * @private
     */
    _showOrderByOptions() {
        showOrderByOptions(this._orderByOptionsElements, this._currentOrderByOption);
    }

    /**
     * Translate the component elements.
     * @returns {void}
     * @private
     */
    _handleTranslationHelper() {
        updateUIFromShadowRoot(this.shadowRoot);

        if (this._orderByButton) {
            this._orderByButton.innerHTML = translate(
                `results.orderBy.${this._currentOrderByOption}`,
            );
        }
    }

    /**
     * Handle order by button click event.
     * @param {Event} event - The click event.
     * @returns {void}
     * @private
     */
    _handleOrderByButtonClick(event) {
        event.stopPropagation();

        const urlParams = new URLSearchParams(window.location.search);
        const hasImgUID =
            urlParams.has(SEARCHER_TYPES.IMAGE_ID) ||
            urlParams.has(SEARCHER_TYPES.IMG_UID);

        if (hasImgUID) return;

        const isHidden =
            this._orderByOptionsContainer.style.display === 'none' ||
            this._orderByOptionsContainer.style.display === '';

        this._orderByOptionsContainer.style.display = isHidden ? 'block' : 'none';
        this._overlayContainer.classList.toggle('hidden-overlay');
    }

    /**
     * Handle overlay click event.
     * @returns {void}
     * @private
     */
    _handleOverlayClick() {
        this._orderByOptionsContainer.style.display = 'none';
        this._overlayContainer.classList.toggle('hidden-overlay');
    }

    /**
     * Handle order by option click event.
     * @param {Event} event - The click event.
     * @returns {void}
     * @private
     */
    _handleOrderByOptionClick(event) {
        event.stopPropagation();

        const option = event.currentTarget;
        const optionValue = option.getAttribute('value');

        updateOrderButtonLabel(
            option,
            optionValue,
            this._orderByOptionsElements,
            this._orderByButton,
        );
        this._currentOrderByOption = optionValue;

        this._orderByOptionsContainer.style.display = 'none';
        this._overlayContainer.classList.toggle('hidden-overlay');

        this._handleTranslationHelper();

        handleOrderByOptionSelection(
            optionValue,
            this._updateCurrentSearcherObj.bind(this),
        );
    }

    /**
     * Handle update order by event.
     * @param {CustomEvent} event - The custom event.
     * @returns {void}
     * @private
     */
    _handleUpdateOrderByEvent(event) {
        this._currentOrderByOption = event.detail.value;
        this._updateCurrentSearcherObj();
        this._addSelectOptionsOrderByEventListeners(true);
    }

    /**
     * Handle update value order by event.
     * @param {CustomEvent} event - The custom event.
     * @returns {void}
     * @private
     */
    _handleUpdateValueOrderByEvent(event) {
        this._handleUpdateOrderByEvent(event);
    }

    /**
     * Handle filter synchronization event.
     * @param {CustomEvent} event - The custom event.
     * @returns {void}
     * @private
     */
    _handleFilterSyncEvent(event) {
        const typeFilterValue = event.detail.typeFilterValue;
        const updatedOrderByOption = handleFilterSync(
            typeFilterValue,
            this._currentOrderByOption,
            this._updateCurrentSearcherObj.bind(this),
        );

        if (updatedOrderByOption !== this._currentOrderByOption) {
            this._currentOrderByOption = updatedOrderByOption;
            this._addSelectOptionsOrderByEventListeners(true);
        }
    }

    /**
     * Handle reset filters event.
     * @returns {void}
     * @private
     */
    _handleResetFiltersEvent() {
        const resetOrderByOption = handleResetFilters(
            this._updateCurrentSearcherObj.bind(this),
        );
        this._currentOrderByOption = resetOrderByOption;
        this._addSelectOptionsOrderByEventListeners(true);
    }

    /**
     * Handle search report details result event.
     * @returns {void}
     * @private
     */
    _handleSearchReportDetailsResultEvent() {
        this._showOrderByOptions();
    }

    /**
     * Handle search image result event.
     * @returns {void}
     * @private
     */
    _handleSearchImgResultEvent() {
        const updatedOrderByOption = handleSearchImageResult(
            this._updateCurrentSearcherObj.bind(this),
            this._handleTranslationHelper.bind(this),
        );

        if (updatedOrderByOption) {
            this._currentOrderByOption = updatedOrderByOption;
        }
    }

    /**
     * Add event listeners to the component elements.
     * @returns {void}
     * @private
     */
    _addEventListeners() {
        document.addEventListener(
            UPDATE_LANGUAGE_EVENT,
            this._handleTranslationHelperBound,
        );
        document.addEventListener(
            UPDATE_ORDER_BY_EVENT,
            this._handleUpdateOrderByEventBound,
        );
        document.addEventListener(
            UPDATE_VALUE_ORDER_BY_EVENT,
            this._handleUpdateValueOrderByEventBound,
        );
        document.addEventListener(
            SYNCH_TYPE_FILTER_SEARCH_OPTIONS_EVENT,
            this._handleFilterSyncEventBound,
        );
        document.addEventListener(
            TRIGGER_RESET_FILTERS_EVENT,
            this._handleResetFiltersEventBound,
        );
        document.addEventListener(
            SEARCH_REPORT_DETAILS_RESULT_EVENT,
            this._handleSearchReportDetailsResultEventBound,
        );
        document.addEventListener(
            SEARCH_IMG_RESULT_EVENT,
            this._handleSearchImgResultEventBound,
        );

        this._addSelectOptionsOrderByEventListeners();
    }

    /**
     * Add event listeners to the select options for order by functionality.
     * @param {boolean} reRender - Whether to re-render the options.
     * @returns {void}
     * @private
     */
    _addSelectOptionsOrderByEventListeners(reRender = false) {
        if (
            !this._orderByButton ||
            !this._orderByOptionsContainer ||
            !this._overlayContainer
        )
            return;

        this._orderByButton.setAttribute('value', this._currentOrderByOption);

        if (reRender) {
            const option = Array.from(this._orderByOptionsElements).find(
                (option) => option.getAttribute('value') === this._currentOrderByOption,
            );

            if (option) {
                updateOrderButtonLabel(
                    option,
                    this._currentOrderByOption,
                    this._orderByOptionsElements,
                    this._orderByButton,
                );
                dispatchDetailIbj(
                    {
                        [SEARCH_IMG_QUERY_PARAMS.SORT_BY_AI]: this._currentOrderByOption,
                    },
                    true,
                );
            }
            return;
        }

        // Remove existing event listeners to prevent duplicates
        this._orderByButton.removeEventListener(
            'click',
            this._handleOrderByButtonClickBound,
        );
        this._overlayContainer.removeEventListener(
            'click',
            this._handleOverlayClickBound,
        );
        this._orderByOptionsElements.forEach((option) => {
            option.removeEventListener('click', this._handleOrderByOptionClickBound);
        });

        // Add event listeners
        this._orderByButton.addEventListener(
            'click',
            this._handleOrderByButtonClickBound,
        );

        this._orderByOptionsElements.forEach((option) => {
            option.setAttribute('data-ripple', '');
            option.addEventListener('click', this._handleOrderByOptionClickBound);
        });

        this._addRippleBehavior();

        this._overlayContainer.addEventListener('click', this._handleOverlayClickBound);
    }

    /**
     * Update the current searcher object.
     * @returns {void}
     * @private
     */
    _updateCurrentSearcherObj() {
        updateCurrentSearcherObj(this._currentOrderByOption);
    }

    /**
     * Check the current searcher object and update the current order by option.
     * @returns {void}
     * @private
     */
    _checkCurrentSearcherObj() {
        this._currentOrderByOption = checkCurrentSearcherObj(
            this._isReportDetailsView,
            this._isReportsView,
        );
        this._updateCurrentSearcherObj();
    }

    /**
     * Remove event listeners when the element is removed from the DOM.
     * @returns {void}
     */
    disconnectedCallback() {
        document.removeEventListener(
            UPDATE_LANGUAGE_EVENT,
            this._handleTranslationHelperBound,
        );
        document.removeEventListener(
            UPDATE_ORDER_BY_EVENT,
            this._handleUpdateOrderByEventBound,
        );
        document.removeEventListener(
            UPDATE_VALUE_ORDER_BY_EVENT,
            this._handleUpdateValueOrderByEventBound,
        );
        document.removeEventListener(
            SYNCH_TYPE_FILTER_SEARCH_OPTIONS_EVENT,
            this._handleFilterSyncEventBound,
        );
        document.removeEventListener(
            TRIGGER_RESET_FILTERS_EVENT,
            this._handleResetFiltersEventBound,
        );
        document.removeEventListener(
            SEARCH_REPORT_DETAILS_RESULT_EVENT,
            this._handleSearchReportDetailsResultEventBound,
        );
        document.removeEventListener(
            SEARCH_IMG_RESULT_EVENT,
            this._handleSearchImgResultEventBound,
        );

        // Remove component-specific event listeners
        if (this._orderByButton) {
            this._orderByButton.removeEventListener(
                'click',
                this._handleOrderByButtonClickBound,
            );
        }
        if (this._overlayContainer) {
            this._overlayContainer.removeEventListener(
                'click',
                this._handleOverlayClickBound,
            );
        }
        if (this._orderByOptionsElements) {
            this._orderByOptionsElements.forEach((option) => {
                option.removeEventListener('click', this._handleOrderByOptionClickBound);
            });
        }
    }

    /**
     * Add the ripple effect to the elements that have the data-ripple attribute
     * @returns {void}
     * @private
     */
    _addRippleBehavior() {
        this.shadowRoot.querySelectorAll('[data-ripple]').forEach((element) => {
            ripple(element);
        });
    }

    /**
     * Get the current order by option.
     * @returns {string} The current order by option.
     */
    get orderByOption() {
        return this._currentOrderByOption;
    }
}

customElements.define('order-by-select-component', OrderBySelectComponent);
